// src/components/Sidebar.jsx
import React, { useContext, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { AuthContext } from "../context/AuthContext";
import { clearAuth, getRole } from "../services/authStorage";
import { normalizeRole, roleMatches } from "../utils/roles";
import logo from "../assets/ECOOPS LOGO_NEW.png";

const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const navigate = useNavigate();
  const { user, logout: contextLogout } = useContext(AuthContext);

  const role = useMemo(
    () => normalizeRole(user?.role || getRole() || "guest"),
    [user?.role]
  );
  const displayName = useMemo(() => {
    if (user?.firstName || user?.lastName) {
      return [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.username || "Account";
    }
    return user?.username || "Account";
  }, [user?.firstName, user?.lastName, user?.username]);

  const shouldCloseOnNavigate = () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  };

  const handleNavigate = () => {
    if (shouldCloseOnNavigate()) onClose?.();
  };

  const handleLogout = () => {
    if (shouldCloseOnNavigate()) onClose?.();
    clearAuth();
    contextLogout?.();
    navigate("/login", { replace: true });
  };

  const navLinkClass = (depth = 0) => ({ isActive }) =>
    `${depth > 0 ? "ml-4" : ""} block rounded-lg px-3 py-2 text-sm transition-colors ${
      isActive ? "bg-slate-200 text-slate-900" : "text-slate-600 hover:bg-slate-100"
    }`;

  const navConfig = [
    // User
    { to: "/dashboard", label: "Dashboard", roles: ["user"] },
    { to: "/portfolio", label: "Portfolio", roles: ["user"] },
    { to: "/performance", label: "Performance", roles: ["user"] },
    { to: "/wallet", label: "Wallet", roles: ["user"] },
    { to: "/transactions", label: "Transactions", roles: ["user"] },
    { to: "/settings", label: "Settings", roles: ["user"] },

    // Admin landing
    { to: "/admin", label: "Admin dashboard", roles: ["admin"] },

    // Compliance
    {
      label: "Compliance",
      roles: ["admin", "compliance"],
      children: [
        { to: "/admin/compliance/dashboard", label: "Dashboard", roles: ["admin", "compliance"] },
        { to: "/admin/compliance", label: "Compliance approvals", roles: ["admin", "compliance"] },
        { to: "/admin/compliance/manual-entry", label: "Manual onboarding", roles: ["admin", "compliance"] },
        { to: "/admin/compliance/staff", label: "Create compliance staff", roles: ["admin", "compliance"] },
        { to: "/admin/compliance/crm", label: "CRM (Approved clients)", roles: ["admin", "compliance"] },
        { to: "/admin/finance/client-360", label: "Client 360", roles: ["admin", "compliance"] },
      ],
    },

    // Investments catalogue (admin only)
    {
      label: "Investments",
      roles: ["admin"],
      children: [
        { to: "/admin/investments", label: "Opportunities", roles: ["admin"] },
      ],
    },

    // Accounts
    {
      label: "Accounts",
      roles: ["admin", "accounts"],
      children: [
        { to: "/admin/accounts/dashboard", label: "Dashboard", roles: ["admin", "accounts"] },
        { to: "/admin/backoffice/postings", label: "Back-office postings", roles: ["admin", "accounts"] },
        { to: "/admin/general-ledger", label: "General ledger", roles: ["admin", "accounts"] },
        { to: "/admin/general-ledger/pending", label: "Pending postings", roles: ["admin", "accounts"] },
        { to: "/admin/finance/bank-accounts", label: "Chart of accounts", roles: ["admin", "accounts"] },
        { to: "/admin/finance/client-360", label: "Client 360", roles: ["admin", "accounts"] },
        { to: "/admin/investments/cashflows", label: "Cashflows", roles: ["admin", "accounts"] },
        { to: "/admin/reports", label: "Reports & exports", roles: ["admin", "accounts"] },
      ],
    },

    // Finance
    {
      label: "Finance",
      roles: ["admin", "finance"],
      children: [
        { to: "/admin/finance/dashboard", label: "Dashboard", roles: ["admin", "finance"] },
        { to: "/admin/finance/client-360", label: "Client 360", roles: ["admin", "finance"] },
        { to: "/admin/investments/subscriptions", label: "Subscribed investments", roles: ["admin", "finance"] },
        { to: "/admin/fixed-income", label: "Fixed income wizard", roles: ["admin", "finance"] },
      ],
    },

    // Firm desk
    {
      label: "Firm desk",
      roles: ["admin", "finance"],
      children: [
        { to: "/admin/firm/investments", label: "Firm investments", roles: ["admin", "finance"] },
        { to: "/admin/firm/reports", label: "Firm reports", roles: ["admin", "finance"] },
      ],
    },
  ];

  const filterNav = (items) =>
    items
      .map((item) => {
        const allowed = !item.roles || (Array.isArray(item.roles) && roleMatches(role, item.roles));
        const children = Array.isArray(item.children) ? filterNav(item.children) : [];
        if (!allowed && children.length === 0) return null;
        return { ...item, children };
      })
      .filter(Boolean);

  const navItems = filterNav(navConfig);

  const renderNavItems = (items, depth = 0) =>
    items.map((item) => (
      <React.Fragment key={item.to || item.label}>
        {item.to ? (
          <NavLink to={item.to} className={navLinkClass(depth)} onClick={handleNavigate}>
            {item.label}
          </NavLink>
        ) : (
          <span className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-500">{item.label}</span>
        )}
        {Array.isArray(item.children) && item.children.length > 0 && (
          <div className="mt-1 space-y-1 border-l border-slate-200 pl-2 ml-2">
            {renderNavItems(item.children, depth + 1)}
          </div>
        )}
      </React.Fragment>
    ));

  const SidebarContent = () => (
    <>
      <div className="mb-6 space-y-3">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Ecoops Wealth" className="h-12 w-auto" />
        </div>
        <div>
          <div className="text-sm text-slate-500 truncate">{displayName}</div>
          <div className="text-xs uppercase tracking-wide text-slate-400">{role}</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto">
        {renderNavItems(navItems)}

        {role === "admin" && (
          <div className="mt-6 space-y-1">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Tools</p>
            <NavLink to="/test-upload" className={navLinkClass(0)} onClick={handleNavigate}>
              Test file upload
            </NavLink>
          </div>
        )}
      </nav>

      <button
        onClick={handleLogout}
        className="mt-6 w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-600 transition hover:bg-slate-100"
      >
        Log out
      </button>
    </>
  );

  return (
    <>
      <aside className="hidden w-64 flex-col border-r border-slate-200 bg-white px-4 py-6 shadow-sm md:flex">
        <SidebarContent />
      </aside>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={onClose} />
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white px-4 py-6 shadow-xl md:hidden">
            <SidebarContent />
          </div>
        </>
      )}
    </>
  );
};

export default Sidebar;
